import React, { useEffect, useRef, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { useTheme } from '@/context/ThemeContext';
import WorkGoalEditor from './WorkGoalEditor';
import { formatGoalElapsed, goalElapsedMs } from './goalElapsed';

const labels = { active: 'Pursuing goal', paused: 'Goal paused', blocked: 'Goal needs input', complete: 'Goal complete' };
const reasons = {
  continuation_limit: 'The continuation limit was reached. Review progress, then resume to keep going.',
  waiting_on_user: 'Answer the pending request to continue.',
  orchestrator_restart: 'The worker restarted. Resume to continue from saved progress.',
  credits_exhausted: 'Add credits, then resume to continue.',
};

export default function WorkGoalBar({ goal, onSend, onControl, disabled = false }) {
  const { colors } = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now);
  const inFlight = useRef(false);
  const locked = busy || disabled;
  useEffect(() => {
    setNow(Date.now());
    if (goal?.status !== 'active') return undefined;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [goal?.id, goal?.status, goal?.activeSince]);
  useEffect(() => { setExpanded(false); setEditTarget(null); setError(''); }, [goal?.id]);
  if (!goal) return null;

  const run = async action => {
    if (locked || inFlight.current) return false;
    inFlight.current = true;
    setBusy(true); setError('');
    try {
      const accepted = await action();
      if (accepted === false) setError('The goal has not changed. Please try again.');
      return accepted;
    } catch (err) { setError(err?.message || 'Could not update the goal. Please try again.'); }
    finally { inFlight.current = false; setBusy(false); }
  };
  const target = value => ({ goalId: value.id, goalRevision: value.revision });
  const iconButton = (label, icon, onPress, inactive = false) => (
    <Pressable accessibilityRole="button" accessibilityLabel={label}
      accessibilityState={{ disabled: locked || inactive }} disabled={locked || inactive}
      onPress={onPress} style={[styles.iconButton, (locked || inactive) && styles.disabled]}>
      <Icon name={icon} size={14} color={colors.text_color_secondary} />
    </Pressable>
  );
  const toggleExpanded = () => { setExpanded(value => !value); setEditTarget(null); };
  return (
    <View testID="work-goal-bar" style={[styles.container, {
      backgroundColor: colors.solid_color_secondary, borderColor: colors.border_color_secondary,
    }]}>
      <View style={styles.row}>
        <Icon name="bullseye" size={16} color={colors.text_color_secondary} />
        <Pressable accessibilityRole="button" accessibilityLabel={`${labels[goal.status]}: ${goal.objective}. Show goal details`}
          accessibilityState={{ expanded, disabled: locked }} disabled={locked} onPress={toggleExpanded} style={styles.objective}>
          <Text size="s" numberOfLines={1} ellipsizeMode="tail">
            <Text size="s" weight="medium">{labels[goal.status] || 'Goal'} </Text>
            <Text size="s" color="secondary">{goal.objective}</Text>
          </Text>
        </Pressable>
        <Text testID="goal-elapsed" size="xs" color="secondary" style={styles.elapsed}>
          {formatGoalElapsed(goalElapsedMs(goal, now))}
        </Text>
        {iconButton('Clear goal', 'trash', () => run(() => onControl('clear', goal)))}
        {goal.status === 'active' && iconButton('Pause goal', 'pause', () => run(() => onControl('pause', goal)))}
        {['paused', 'blocked'].includes(goal.status) && iconButton('Resume goal', 'play',
          () => run(() => onSend('/goal resume', [], target(goal))), goal.reason === 'waiting_on_user')}
        {iconButton(expanded ? 'Collapse goal' : 'Expand goal', expanded ? 'compress' : 'expand', toggleExpanded)}
      </View>
      {expanded && (editTarget ? (
        <WorkGoalEditor initialObjective={editTarget.objective} editing disabled={locked}
          onCancel={() => setEditTarget(null)} onSubmit={objective => run(async () => {
            const accepted = await onSend('/goal set ' + objective, [], target(editTarget));
            if (accepted !== false) setEditTarget(null);
            return accepted;
          })} />
      ) : <View style={styles.details}>
        <Text size="s" selectable>{goal.objective}</Text>
        {!!(goal.summary || reasons[goal.reason]) && <Text size="xs" color="secondary">{reasons[goal.reason] || goal.summary}</Text>}
        <Pressable accessibilityRole="button" accessibilityLabel="Edit goal" disabled={locked}
          onPress={() => setEditTarget(goal)} style={styles.editButton}>
          <Text size="s" color="secondary">Edit goal</Text>
        </Pressable>
      </View>)}
      {!!error && <View style={styles.details}><Text size="xs" accessibilityRole="alert">{error}</Text></View>}
    </View>
  );
}

const styles = StyleSheet.create({
  container: { marginHorizontal: 12, marginBottom: -14, paddingBottom: 14, borderWidth: 1,
    borderTopLeftRadius: 16, borderTopRightRadius: 16, overflow: 'hidden' },
  row: { minHeight: 44, flexDirection: 'row', alignItems: 'center', paddingLeft: 12, paddingRight: 4, gap: 6 },
  objective: { flex: 1, minWidth: 0, minHeight: 44, justifyContent: 'center' },
  elapsed: { flexShrink: 0, fontVariant: ['tabular-nums'] },
  iconButton: { width: 36, minHeight: 44, alignItems: 'center', justifyContent: 'center' },
  disabled: { opacity: 0.45 },
  details: { paddingHorizontal: 14, paddingBottom: 12, gap: 8 },
  editButton: { minHeight: 40, justifyContent: 'center', alignSelf: 'flex-start' },
});
